import { useState, useEffect, useRef } from 'react';
import { Play, Pause, Download, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface LogEntry {
  id: string;
  timestamp: string;
  level: 'debug' | 'info' | 'warn' | 'error';
  service: string;
  message: string;
  details?: any;
}

interface LogViewerProps {
  source?: string;
  filters?: any;
  height?: number;
  follow?: boolean;
}

// Mock log data
const generateMockLogs = (): LogEntry[] => [
  {
    id: '1',
    timestamp: new Date().toISOString(),
    level: 'info',
    service: 'vapi',
    message: 'Call initiated for user +1234567890'
  },
  {
    id: '2',
    timestamp: new Date(Date.now() - 5000).toISOString(),
    level: 'debug',
    service: 'n8n',
    message: 'Workflow execution started: lead_qualification_v2'
  },
  {
    id: '3',
    timestamp: new Date(Date.now() - 10000).toISOString(),
    level: 'warn',
    service: 'openrouter',
    message: 'High latency detected: 2.3s response time'
  },
  {
    id: '4',
    timestamp: new Date(Date.now() - 15000).toISOString(),
    level: 'error',
    service: 'vapi',
    message: 'Failed to process voice command: timeout after 10s'
  },
  {
    id: '5',
    timestamp: new Date(Date.now() - 20000).toISOString(),
    level: 'info',
    service: 'supabase',
    message: 'Database connection pool scaled to 15 connections'
  }
];

interface LogLineProps {
  log: LogEntry;
  searchTerm?: string;
}

function LogLine({ log, searchTerm }: LogLineProps) {
  const levelColors = {
    debug: 'text-gray-500',
    info: 'text-blue-400',
    warn: 'text-yellow-400',
    error: 'text-red-400'
  };

  const serviceColors = {
    vapi: 'text-purple-400',
    n8n: 'text-green-400',
    openrouter: 'text-orange-400',
    supabase: 'text-cyan-400'
  };

  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleTimeString('en-US', { 
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  const highlightText = (text: string, searchTerm?: string) => {
    if (!searchTerm) return text;
    
    const regex = new RegExp(`(${searchTerm})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 text-black">
          {part}
        </span>
      ) : part
    );
  };

  return (
    <div className="flex gap-2 hover:bg-gray-900 px-2 py-1 text-xs font-mono">
      <span className="text-gray-600 w-20 flex-shrink-0">
        {formatTimestamp(log.timestamp)}
      </span>
      <span className={cn('w-12 flex-shrink-0', levelColors[log.level])}>
        [{log.level.toUpperCase()}]
      </span>
      <span className={cn('w-16 flex-shrink-0', serviceColors[log.service as keyof typeof serviceColors] || 'text-gray-400')}>
        {log.service}
      </span>
      <span className="flex-1">
        {highlightText(log.message, searchTerm)}
      </span>
    </div>
  );
}

export function LogViewer({ 
  source = 'system', 
  filters, 
  height = 400,
  follow = true 
}: LogViewerProps) {
  const [logs, setLogs] = useState<LogEntry[]>(generateMockLogs);
  const [isFollowing, setIsFollowing] = useState(follow);
  const [searchTerm, setSearchTerm] = useState('');
  const [levelFilter, setLevelFilter] = useState<string>('all');
  const [serviceFilter, setServiceFilter] = useState<string>('all');
  const scrollRef = useRef<HTMLDivElement>(null);

  // Simulate log streaming
  useEffect(() => {
    if (!isFollowing) return;

    const interval = setInterval(() => {
      const newLog: LogEntry = {
        id: Date.now().toString(),
        timestamp: new Date().toISOString(),
        level: ['debug', 'info', 'warn', 'error'][Math.floor(Math.random() * 4)] as LogEntry['level'],
        service: ['vapi', 'n8n', 'openrouter', 'supabase'][Math.floor(Math.random() * 4)],
        message: `Sample log message ${Date.now()}`
      };

      setLogs(prev => [...prev.slice(-999), newLog]); // Keep last 1000 logs
    }, 3000);

    return () => clearInterval(interval);
  }, [isFollowing]);

  // Auto-scroll when following
  useEffect(() => {
    if (isFollowing && scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [logs, isFollowing]);

  const filteredLogs = logs.filter(log => {
    const matchesSearch = !searchTerm || 
      log.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      log.service.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesLevel = levelFilter === 'all' || log.level === levelFilter;
    const matchesService = serviceFilter === 'all' || log.service === serviceFilter;

    return matchesSearch && matchesLevel && matchesService;
  });

  const exportLogs = () => {
    const logsText = filteredLogs.map(log => 
      `${log.timestamp} [${log.level.toUpperCase()}] ${log.service}: ${log.message}`
    ).join('\n');
    
    const blob = new Blob([logsText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `logs-${source}-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const clearLogs = () => {
    setLogs([]);
  };

  return (
    <div className="space-y-3">
      {/* Controls */}
      <div className="flex items-center gap-2 text-sm">
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => setIsFollowing(!isFollowing)}
          >
            {isFollowing ? <Pause className="w-3 h-3" /> : <Play className="w-3 h-3" />}
            {isFollowing ? 'Pause' : 'Follow'}
          </Button>
          
          <Button size="sm" variant="outline" onClick={exportLogs}>
            <Download className="w-3 h-3 mr-1" />
            Export
          </Button>
          
          <Button size="sm" variant="outline" onClick={clearLogs}>
            Clear
          </Button>
        </div>
        
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-xs">
            <Search className="w-3 h-3 absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <Input
              placeholder="Search logs..."
              className="pl-7 h-8 text-xs"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <Select value={levelFilter} onValueChange={setLevelFilter}>
            <SelectTrigger className="w-24 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Levels</SelectItem>
              <SelectItem value="debug">Debug</SelectItem>
              <SelectItem value="info">Info</SelectItem>
              <SelectItem value="warn">Warn</SelectItem>
              <SelectItem value="error">Error</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={serviceFilter} onValueChange={setServiceFilter}>
            <SelectTrigger className="w-28 h-8 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Services</SelectItem>
              <SelectItem value="vapi">Vapi</SelectItem>
              <SelectItem value="n8n">n8n</SelectItem>
              <SelectItem value="openrouter">OpenRouter</SelectItem>
              <SelectItem value="supabase">Supabase</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Log Display */}
      <div 
        ref={scrollRef}
        className="bg-black text-gray-300 p-4 rounded-lg overflow-auto border"
        style={{ height }}
      >
        {filteredLogs.length === 0 ? (
          <div className="text-center text-gray-500 text-sm mt-8">
            No logs match the current filters
          </div>
        ) : (
          filteredLogs.map((log) => (
            <LogLine key={log.id} log={log} searchTerm={searchTerm} />
          ))
        )}
      </div>
      
      {/* Status Bar */}
      <div className="flex items-center justify-between text-xs text-muted-foreground">
        <span>{filteredLogs.length} logs visible</span>
        <span>
          Following: {isFollowing ? 'ON' : 'OFF'} | 
          Source: {source} | 
          Last update: {new Date().toLocaleTimeString()}
        </span>
      </div>
    </div>
  );
}